import type { AppNotification } from '@appTypes/shell';
import { NotificationsActiveOutlined, NotificationsOutlined, ReceiptLongOutlined, SchoolOutlined } from '@mui/icons-material';
import { Box, Button, CircularProgress, Popover, Typography } from '@mui/material';
import { notificationService } from '@services/notification.service';
import { useAppShellStore } from '@store/appShellStore';
import { theme } from '@styles/theme';
import { useState } from 'react';

const NOTIF_CFG: Record<string, { bg: string; color: string }> = {
  new_admission:   { bg: theme.palette.status.success.bg, color: theme.palette.status.success.dot },
  new_installment: { bg: theme.palette.status.info.bg, color: theme.palette.status.info.dot },
  reminder:        { bg: '#fff7ed', color: theme.palette.status.warning.dot },
};

const getNotifIcon = (type: string) => {
  if (type === 'new_admission')   return <SchoolOutlined sx={{ fontSize: 14 }} />;
  if (type === 'new_installment') return <ReceiptLongOutlined sx={{ fontSize: 14 }} />;
  return <NotificationsActiveOutlined sx={{ fontSize: 14 }} />;
};

const fmtRelTime = (iso: string): string => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export const NotificationBell = () => {
  const unreadCount = useAppShellStore((s) => s.unreadCount);
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);

  const loadNotifications = async () => {
    setNotifLoading(true);
    try {
      const { data } = await notificationService.getNotifications({ page: 1, limit: 20 });
      setNotifications(data.items);
      useAppShellStore.getState().setUnreadCount(data.unreadCount);
    } catch {}
    finally { setNotifLoading(false); }
  };

  const handleBellClick = (e: React.MouseEvent<HTMLElement>) => {
    setNotifAnchor(e.currentTarget);
    loadNotifications();
  };

  const handleMarkRead = async (id: string) => {
    try {
      await notificationService.markRead(id);
      setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, read: true } : n)));
      const stillUnread = notifications.filter((n) => n._id !== id && !n.read).length;
      useAppShellStore.getState().setUnreadCount(stillUnread);
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      useAppShellStore.getState().setUnreadCount(0);
    } catch {}
  };

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Box
          onClick={handleBellClick}
          sx={(theme) => ({
            width: 45.437,
            height: 45.437,
            bgcolor: theme.palette.grey[50],
            border: `1.262px solid ${theme.palette.grey[50]}`,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover': { bgcolor: theme.palette.grey[100] },
          })}
        >
          <NotificationsOutlined sx={(theme) => ({ fontSize: 19, color: theme.palette.grey[700] })} />
        </Box>
        {unreadCount > 0 && (
          <Box
            sx={(theme) => ({
              position: 'absolute',
              top: -2.52,
              right: 0,
              minWidth: 17.67,
              height: 17.67,
              bgcolor: theme.palette.error.main,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: '2px',
              pointerEvents: 'none',
            })}
          >
            <Typography sx={{ fontSize: '10px', fontWeight: 700, color: 'white', lineHeight: 1 }}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </Typography>
          </Box>
        )}
      </Box>

      <Popover
        open={Boolean(notifAnchor)}
        anchorEl={notifAnchor}
        onClose={() => setNotifAnchor(null)}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: { xs: 'calc(100vw - 24px)', sm: 360 }, maxWidth: 'calc(100vw - 24px)', maxHeight: 480, borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.12)', mt: '6px' } } }}
      >
        {/* Header */}
        <Box sx={(theme) => ({ px: '16px', py: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${theme.palette.grey[200]}` })}>
          <Typography sx={(theme) => ({ fontSize: '14px', fontWeight: 700, color: theme.palette.grey[900] })}>
            Notifications{unreadCount > 0 ? ` (${unreadCount})` : ''}
          </Typography>
          {unreadCount > 0 && (
            <Button
              onClick={handleMarkAllRead}
              size="small"
              sx={(theme) => ({ fontSize: '12px', fontWeight: 500, color: theme.palette.primary.main, textTransform: 'none', p: 0, minWidth: 0 })}
            >
              Mark all read
            </Button>
          )}
        </Box>

        {/* List */}
        <Box sx={{ overflowY: 'auto', maxHeight: 400 }}>
          {notifLoading ? (
            <Box sx={{ p: '24px', display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={22} sx={(theme) => ({ color: theme.palette.primary.main })} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: '32px', textAlign: 'center' }}>
              <NotificationsOutlined sx={(theme) => ({ fontSize: 32, color: theme.palette.grey[300], display: 'block', mx: 'auto', mb: '8px' })} />
              <Typography sx={(theme) => ({ fontSize: '13px', color: theme.palette.slate[400] })}>No notifications</Typography>
            </Box>
          ) : (
            notifications.map((n) => {
              const cfg = NOTIF_CFG[n.type] ?? NOTIF_CFG.reminder;
              return (
                <Box
                  key={n._id}
                  onClick={() => !n.read && handleMarkRead(n._id)}
                  sx={(theme) => ({
                    px: '16px',
                    py: '12px',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                    bgcolor: n.read ? 'transparent' : theme.palette.status.info.bg,
                    cursor: n.read ? 'default' : 'pointer',
                    '&:hover': { bgcolor: n.read ? theme.palette.grey[50] : '#dbeafe' },
                    borderBottom: `1px solid ${theme.palette.slate[100]}`,
                  })}
                >
                  <Box
                    sx={{
                      width: 32, height: 32, borderRadius: '50%',
                      bgcolor: cfg.bg, color: cfg.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}
                  >
                    {getNotifIcon(n.type)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={(theme) => ({ fontSize: '13px', fontWeight: n.read ? 400 : 600, color: theme.palette.grey[900], lineHeight: '18px' })}>
                      {n.message}
                    </Typography>
                    <Typography sx={(theme) => ({ fontSize: '11px', color: theme.palette.grey[400], mt: '2px' })}>
                      {fmtRelTime(n.createdAt)}
                    </Typography>
                  </Box>
                  {!n.read && (
                    <Box sx={(theme) => ({ width: 8, height: 8, borderRadius: '50%', bgcolor: theme.palette.primary.main, flexShrink: 0, mt: '5px' })} />
                  )}
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
};
